import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import {
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  Activity,
  MessageSquare,
  Save,
  X,
  History,
  Clock,
  Search,
  ArrowRightLeft,
  ArrowDownToLine,
} from "lucide-react";
import { formatCurrency } from "@/lib/utils/formatters";
import { toast } from "sonner";
import { useCustomerContactsList } from "@/hooks/useCustomerContacts";
import { useCompanyUsersList } from "@/hooks/useUsersData";
import { useCompany } from "@/contexts/CompanyContext";
import { useUserContext } from "@/hooks/useUserContext";
import { useCreateCollectionCase, mapPriorityToId } from "@/hooks/useCollectionCasesData";
import { type Transaction as TransactionFull, type ColumnVisibility } from "@/types/transaction";
import { useNavigate } from "react-router-dom";
import { CustomerSearchCombobox } from "@/components/shared/customer-search-combobox";
import type { CreatePromiseFormCustomer } from "@/hooks/useCreatePromiseData";
import { TransactionTable } from "@/components/transactions/transaction-table";
import { TransactionDetailSheet } from "@/components/transactions/transaction-detail-sheet";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import {
  useCreatePromiseTransactionsPaginatedGraphQL,
  buildCreatePromiseTransactionExportFilter,
  CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP,
} from "@/hooks/useCreatePromiseTransactionsGraphQL";
import { useExportAll } from "@/hooks/useExportAll";
import { Forms } from "@/types/export";
import type { ColumnInput } from "@/types/export";
import { ExportProvider } from "@/providers/ExportProvider";

interface CreateCaseDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCaseCreated?: () => void;
  prePopulatedCustomer?: {
    customerId: string;
    customerName: string;
    accountNumber: string;
  };
  selectedTransactions?: TransactionFull[];
  triggerSource?: 'transactions-page' | 'customer-transactions';
}

interface NewCaseData {
  customerName: string;
  customerAccountNumber: string;
  customerEmail: string;
  customerPhone: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Active' | 'On Hold' | 'Closed' | 'Escalated';
  strategyId: string;
  assignedTo?: string;
  notes: string;
  nextAction: string;
  nextActionDate: string;
}

function getContactPlaceholder(
  hasCustomer: boolean,
  contactCount: number,
): string {
  if (!hasCustomer) return 'Select a customer first';
  if (contactCount === 0) return 'No contacts available';
  return 'Select a contact';
}

const DEFAULT_CASE_DRAWER_TX_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference Number', visible: true, order: 0, locked: true },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'transactionType', label: 'Type', visible: true, order: 3 },
  { id: 'amount', label: 'Gross', visible: true, order: 4 },
  { id: 'currency', label: 'Currency', visible: true, order: 5 },
  { id: 'outstandingAmount', label: 'Outstanding', visible: true, order: 6 },
  { id: 'status', label: 'Status', visible: true, order: 7 },
  { id: 'description', label: 'Description', visible: false, order: 8 },
  { id: 'accountCode', label: 'Account', visible: false, order: 9 },
  { id: 'netAmount', label: 'Net', visible: false, order: 10 },
  { id: 'vatAmount', label: 'Tax', visible: false, order: 11 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 12 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 13 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 14 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 15 },
  { id: 'customerName', label: 'Customer Name', visible: false, order: 16 },
  { id: 'flags', label: 'Flags', visible: true, order: 17 },
  { id: 'onDispute', label: 'External Dispute', visible: false, order: 18 },
  { id: 'actions', label: 'Actions', visible: true, order: 19 },
];

export function CreateCaseDrawer({ open, onOpenChange, onCaseCreated, prePopulatedCustomer, selectedTransactions: preSelectedTransactions, triggerSource }: Readonly<CreateCaseDrawerProps>) {
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const createCaseMutation = useCreateCollectionCase();
  const [activeTab, setActiveTab] = useState("overview");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<CreatePromiseFormCustomer | null>(null);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const skipChangeDetection = useRef(false);

  // Transaction tab state
  const [transactionSearch, setTransactionSearch] = useState("");
  const [sortBy, setSortBy] = useState<string>('transactionDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [txCurrentPage, setTxCurrentPage] = useState(1);
  const [txItemsPerPage, setTxItemsPerPage] = useState(25);
  const [transactionDetailSheetOpen, setTransactionDetailSheetOpen] = useState(false);
  const [selectedTransactionDetail, setSelectedTransactionDetail] = useState<TransactionFull | null>(null);

  // Fetch active users for the Assigned To dropdown
  const { data: activeUsers = [] } = useCompanyUsersList();

  // Get logged-in user context to default the Assigned To field
  const { data: userContext } = useUserContext();

  // Derive customer ID for contacts API lookup and transactions
  const contactsCustomerId = preSelectedTransactions?.[0]?.customerId?.toString()
    ?? prePopulatedCustomer?.customerId
    ?? selectedCustomer?.id
    ?? '';
  const { data: apiContacts = [] } = useCustomerContactsList(contactsCustomerId);

  // Form data state
  const [formData, setFormData] = useState<NewCaseData>({
    customerName: prePopulatedCustomer?.customerName || "",
    customerAccountNumber: prePopulatedCustomer?.accountNumber || "",
    customerEmail: "",
    customerPhone: "",
    priority: "Medium",
    status: "Active",
    strategyId: "strategy-1",
    assignedTo: undefined,
    notes: "",
    nextAction: "Follow-up call",
    nextActionDate: format(new Date(), "yyyy-MM-dd"),
  });

  // Default Assigned To to the logged-in user
  useEffect(() => {
    if (!userContext?.internalUserId || activeUsers.length === 0) return;
    const loggedInUserId = String(userContext.internalUserId);
    const userExists = activeUsers.some(u => u.id === loggedInUserId);
    if (userExists && !formData.assignedTo) {
      setFormData(prev => ({ ...prev, assignedTo: loggedInUserId }));
    }
  }, [userContext?.internalUserId, activeUsers, formData.assignedTo]);

  // Filter pre-selected transactions to a single currency (use the first transaction's currency)
  const filteredPreSelectedTransactions = useMemo(() => {
    if (!preSelectedTransactions || preSelectedTransactions.length === 0) return preSelectedTransactions;
    if (triggerSource !== 'transactions-page' && triggerSource !== 'customer-transactions') return preSelectedTransactions;

    const firstCurrency = preSelectedTransactions.find(t => t.currency)?.currency;
    if (!firstCurrency) return preSelectedTransactions;

    return preSelectedTransactions.filter(t => !t.currency || t.currency === firstCurrency);
  }, [preSelectedTransactions, triggerSource]);

  // When triggered from transactions page or customer transactions with pre-selected transactions, filter to only those
  const actionBarTransactionIds = useMemo(() => {
    if ((triggerSource === 'transactions-page' || triggerSource === 'customer-transactions')
        && filteredPreSelectedTransactions && filteredPreSelectedTransactions.length > 0) {
      return filteredPreSelectedTransactions.map(t => Number(t.id));
    }
    return undefined;
  }, [triggerSource, filteredPreSelectedTransactions]);

  // GraphQL-based transaction data
  const {
    transactions: graphqlTransactions,
    pagination: txPagination,
  } = useCreatePromiseTransactionsPaginatedGraphQL(
    contactsCustomerId || undefined,
    {
      page: txCurrentPage,
      pageSize: txItemsPerPage,
      search: transactionSearch || undefined,
      sortBy,
      sortOrder,
      transactionIds: actionBarTransactionIds,
    },
  );

  // Column configuration
  const {
    columns: caseTxCols,
    savePreferences: saveCaseTxPrefs,
    resetToDefaults: resetCaseTxPrefs,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.CreateCaseDrawerTransactions, DEFAULT_CASE_DRAWER_TX_COLUMNS);
  const columnVisibility = useMemo(() => {
    const vis = {} as ColumnVisibility;
    caseTxCols.forEach((col) => { vis[col.id as keyof ColumnVisibility] = col.visible; });
    return vis;
  }, [caseTxCols]);

  // Server-side XLSX export
  const getExportFilter = useCallback((): Record<string, unknown> | undefined => {
    return buildCreatePromiseTransactionExportFilter(contactsCustomerId || undefined, transactionSearch || undefined, actionBarTransactionIds);
  }, [contactsCustomerId, transactionSearch, actionBarTransactionIds]);

  const getExportSummary = useCallback((): string | undefined => {
    return transactionSearch || undefined;
  }, [transactionSearch]);

  const getVisibleExportColumns = useCallback((): ColumnInput[] => {
    return caseTxCols
      .filter((col) => col.visible && col.id in CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP)
      .map((col) => ({
        name: CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
        displayName: col.label,
      }));
  }, [caseTxCols]);

  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_CASE_DRAWER_TX_COLUMNS
      .filter((col) => col.id in CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP)
      .map((col) => ({
        name: CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
        displayName: col.label,
      }));
  }, []);

  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: Forms.CreatePromiseTransactions,
    getColumns: getVisibleExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `case-drawer-transactions-${selectedCustomer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: Forms.CreatePromiseTransactions,
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `case-drawer-transactions-all-columns-${selectedCustomer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const handleExportCurrentView = useCallback(() => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportCurrentView]);

  const handleExportAllColumns = useCallback(() => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportAllColumns]);

  // Auto-populate form when drawer opens with selected transactions
  useEffect(() => {
    if (open && filteredPreSelectedTransactions && filteredPreSelectedTransactions.length > 0) {
      skipChangeDetection.current = true;
      const firstTransaction = filteredPreSelectedTransactions[0];

      // Auto-populate customer details from first transaction
      setFormData(prev => ({
        ...prev,
        customerName: firstTransaction.customerName || "",
        customerAccountNumber: firstTransaction.accountNumber || '',
      }));

      // Auto-select transactions when coming from transactions page or customer transactions
      if (triggerSource === 'transactions-page' || triggerSource === 'customer-transactions') {
        const transactionIds = new Set(filteredPreSelectedTransactions.map(t => t.id.toString()));
        setSelectedTransactions(transactionIds);

        // Notify user if some transactions were excluded due to multi-currency
        if (preSelectedTransactions && filteredPreSelectedTransactions.length < preSelectedTransactions.length) {
          const excludedCount = preSelectedTransactions.length - filteredPreSelectedTransactions.length;
          const caseCurrency = firstTransaction.currency;
          toast.info(`Transactions filtered to ${caseCurrency} currency. ${excludedCount} transaction(s) in other currencies were excluded.`);
        }
      }

      // Auto-select primary contact if available
      const primaryContact = apiContacts.find(c => c.isPrimary);
      if (primaryContact) {
        setSelectedContactId(primaryContact.id);
        setFormData(prev => ({
          ...prev,
          customerEmail: primaryContact.email || "",
          customerPhone: primaryContact.phone || "",
        }));
      }
    }
  }, [open, filteredPreSelectedTransactions, preSelectedTransactions, apiContacts, triggerSource]);

  // Track unsaved changes
  useEffect(() => {
    if (skipChangeDetection.current) {
      skipChangeDetection.current = false;
      return;
    }

    const hasChanges =
      formData.customerEmail.trim() !== "" ||
      formData.customerPhone.trim() !== "" ||
      formData.notes.trim() !== "" ||
      selectedTransactions.size > 0 ||
      formData.priority !== "Medium" ||
      formData.status !== "Active" ||
      formData.strategyId !== "strategy-1" ||
      formData.assignedTo !== undefined ||
      formData.nextAction !== "Follow-up call";

    setHasUnsavedChanges(hasChanges);
  }, [formData, selectedTransactions]);

  // Handle customer selection from combobox
  const handleCustomerSelect = (customer: CreatePromiseFormCustomer) => {
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      customerAccountNumber: customer.accountNumber,
      customerName: customer.name,
      customerEmail: '',
      customerPhone: '',
    });
    setSelectedContactId("");
    setSelectedTransactions(new Set());
    setTxCurrentPage(1);
    setHasUnsavedChanges(true);
  };

  const resetFormState = () => {
    const defaultAssignedTo = userContext?.internalUserId
      ? String(userContext.internalUserId)
      : undefined;
    setFormData({
      customerName: prePopulatedCustomer?.customerName || "",
      customerAccountNumber: prePopulatedCustomer?.accountNumber || "",
      customerEmail: "",
      customerPhone: "",
      priority: "Medium",
      status: "Active",
      strategyId: "strategy-1",
      assignedTo: defaultAssignedTo,
      notes: "",
      nextAction: "Follow-up call",
      nextActionDate: format(new Date(), "yyyy-MM-dd"),
    });
    setSelectedTransactions(new Set());
    setActiveTab("overview");
    setSelectedContactId("");
    setSelectedCustomer(null);
    setHasUnsavedChanges(false);
    setTransactionSearch("");
    setSortBy('transactionDate');
    setSortOrder('desc');
    setTxCurrentPage(1);
    setTxItemsPerPage(25);
    setTransactionDetailSheetOpen(false);
    setSelectedTransactionDetail(null);
  };

  // Reset form when dialog opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Check for unsaved changes before closing
      if (hasUnsavedChanges) {
        setShowCloseConfirmation(true);
        return;
      }

      // Reset form
      resetFormState();
    } else {
      // Auto-populate primary contact if customer is pre-populated
      if (prePopulatedCustomer) {
        skipChangeDetection.current = true;
        const primaryContact = apiContacts.find((c) => c.isPrimary);
        if (primaryContact) {
          setSelectedContactId(primaryContact.id);
          setFormData({
            customerName: prePopulatedCustomer.customerName,
            customerAccountNumber: prePopulatedCustomer.accountNumber,
            customerEmail: primaryContact.email,
            customerPhone: primaryContact.phone,
            priority: "Medium",
            status: "Active",
            strategyId: "strategy-1",
            assignedTo: undefined,
            notes: "",
            nextAction: "Follow-up call",
            nextActionDate: format(new Date(), "yyyy-MM-dd"),
          });
        }
      }
    }
    onOpenChange(newOpen);
  };

  // Confirm close without saving
  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    setHasUnsavedChanges(false);
    resetFormState();
    onOpenChange(false);
  };

  const handleContactChange = (contactId: string) => {
    setSelectedContactId(contactId);
    const selectedContact = apiContacts.find((c) => c.id === contactId);
    if (selectedContact) {
      setFormData({
        ...formData,
        customerEmail: selectedContact.email,
        customerPhone: selectedContact.phone,
      });
    }
  };

  // Transaction table handlers
  const handleViewTransactionDetail = useCallback((transaction: TransactionFull) => {
    setSelectedTransactionDetail(transaction);
    setTransactionDetailSheetOpen(true);
  }, []);

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    saveCaseTxPrefs(columns);
  };

  // Total Outstanding from selected transactions only (uses DisplayOutstandingAmount)
  const selectedOutstandingTotal = useMemo(() => {
    return graphqlTransactions
      .filter(x => selectedTransactions.has(String(x.id)))
      .reduce((sum, x) => sum + (x.displayOutstandingAmount ?? x.outstandingAmount ?? x.amount), 0);
  }, [graphqlTransactions, selectedTransactions]);

  // Derive the selected currency from the currently selected transactions
  const selectedCurrency = useMemo(() => {
    for (const txId of Array.from(selectedTransactions)) {
      const tx = graphqlTransactions.find(x => String(x.id) === txId);
      if (tx?.currency) return tx.currency;
    }
    return undefined;
  }, [selectedTransactions, graphqlTransactions]);

  // Currency-aware single row toggle
  const handleToggleTransaction = useCallback((id: number, checked: boolean) => {
    const txId = String(id);
    if (checked) {
      const tx = graphqlTransactions.find(x => String(x.id) === txId);
      if (tx?.currency && selectedCurrency && tx.currency !== selectedCurrency) {
        toast.error(`Cannot mix currencies. Selected transactions use ${selectedCurrency} but the selected transaction is in ${tx.currency}.`);
        return;
      }
    }
    const newSelected = new Set(selectedTransactions);
    if (checked) {
      newSelected.add(txId);
    } else {
      newSelected.delete(txId);
    }
    setSelectedTransactions(newSelected);
  }, [graphqlTransactions, selectedTransactions, selectedCurrency]);

  const handleSelectAllTransactions = useCallback(() => {
    const baseCurrency = selectedCurrency
      ?? graphqlTransactions.find(x => x.currency)?.currency;
    const allIds = new Set(selectedTransactions);
    let skipped = false;
    for (const tx of graphqlTransactions) {
      if (baseCurrency && tx.currency && tx.currency !== baseCurrency) {
        skipped = true;
        continue;
      }
      allIds.add(String(tx.id));
    }
    setSelectedTransactions(allIds);
    if (skipped) {
      toast.error(`Some transactions were skipped because they use a different currency than the already selected transactions (${baseCurrency}).`);
    }
  }, [graphqlTransactions, selectedTransactions, selectedCurrency]);

  const handleDeselectAllTransactions = useCallback(() => {
    const currentPageIds = new Set(graphqlTransactions.map(x => String(x.id)));
    setSelectedTransactions(prev => {
      const newSelected = new Set(prev);
      currentPageIds.forEach(id => newSelected.delete(id));
      return newSelected;
    });
  }, [graphqlTransactions]);


  const onSortChange = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setTxCurrentPage(1);
  }, []);

  const hasCustomer = !!(prePopulatedCustomer || (preSelectedTransactions && preSelectedTransactions.length > 0) || selectedCustomer);

  const validateForm = (): boolean => {
    if (!formData.customerAccountNumber.trim()) {
      toast.error("Please select a customer");
      setActiveTab("overview");
      return false;
    }

    // At least one transaction must be selected - check early before other fields
    if (selectedTransactions.size === 0) {
      toast.error("Please select at least one transaction");
      setActiveTab("overview");
      return false;
    }

    // Currency consistency validation
    const selectedTxs = graphqlTransactions.filter(x => selectedTransactions.has(String(x.id)));
    const currencies = [...new Set(selectedTxs.map(x => x.currency).filter(Boolean))];
    if (currencies.length > 1) {
      toast.error(`All transactions must share the same currency. Found: ${currencies.join(', ')}`);
      setActiveTab("overview");
      return false;
    }

    // Email format validation (only if email is provided)
    if (formData.customerEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.customerEmail)) {
        toast.error("Please enter a valid email address");
        setActiveTab("overview");
        return false;
      }
    }

    if (!formData.nextAction.trim()) {
      toast.error("Next action is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.nextActionDate) {
      toast.error("Next action date is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.assignedTo) {
      toast.error("Assigned To is required");
      setActiveTab("overview");
      return false;
    }

    return true;
  };

  const handleSaveCase = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const totalAmount = selectedOutstandingTotal;
      const selectedTransactionIds = Array.from(selectedTransactions).map(Number);

      const numericCustomerId = preSelectedTransactions?.[0]?.customerId
        ?? (prePopulatedCustomer ? parseInt(prePopulatedCustomer.customerId, 10) : null)
        ?? (selectedCustomer ? parseInt(selectedCustomer.id, 10) : NaN);

      if (isNaN(numericCustomerId)) {
        toast.error("Unable to determine customer ID");
        return;
      }

      const selectedTxsForSave = graphqlTransactions.filter(x => selectedTransactions.has(String(x.id)));
      const currenciesForSave = [...new Set(selectedTxsForSave.map(x => x.currency).filter(Boolean))];
      const currencyCode = currenciesForSave.length === 1 ? currenciesForSave[0] : undefined;

      const response = await createCaseMutation.mutateAsync({
        customerId: numericCustomerId,
        priorityId: mapPriorityToId(formData.priority),
        strategyId: undefined,
        amount: totalAmount,
        nextAction: formData.nextAction,
        nextActionDate: formData.nextActionDate || undefined,
        notes: formData.notes || undefined,
        transactionIds: selectedTransactionIds.length > 0 ? selectedTransactionIds : undefined,
        assignedToUserId: Number(formData.assignedTo),
        contactId: selectedContactId ? Number(selectedContactId) : undefined,
        currencyCode,
      });

      toast.success('Collection case created', {
        description: (
          <span>
            Case{' '}
            <a
              href={`/collections/queue/case/${response.caseId}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/collections/queue/case/${response.caseId}`);
              }}
              style={{ color: '#0d9488', textDecoration: 'underline', fontWeight: 500 }}
            >
              {response.caseNumber}
            </a>
            {' '}created successfully
          </span>
        ),
        duration: 8000,
      });

      if (onCaseCreated) {
        onCaseCreated();
      }

      setHasUnsavedChanges(false);
      resetFormState();
      onOpenChange(false);
    } catch (error) {
      toast.error(`Failed to create case: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[1100px] p-0 flex flex-col" hideCloseButton>
      <ExportProvider>
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div>
                <SheetTitle className="text-3xl font-bold tracking-tight">Create New Case</SheetTitle>
                <p className="text-muted-foreground mt-1">
                  Enter customer and case information to create a new collection case
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="default"
                onClick={() => handleOpenChange(false)}
                disabled={isSaving}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                size="default"
                onClick={handleSaveCase}
                disabled={isSaving}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {isSaving ? "Creating..." : "Create Case"}
              </Button>
            </div>
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-4 mx-6 mt-4 shrink-0">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions" className="flex items-center gap-2">
              Transactions
              {selectedTransactions.size > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                  {selectedTransactions.size}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto px-6 pb-6 mt-6">
            <TabsContent value="overview" className="space-y-6 mt-0">
              {/* Two Column Layout - Customer & Case Info */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Customer Information & Contact */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Customer Information
                    </CardTitle>
                    <CardDescription>Contact details and customer profile</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      {/* Customer Selection */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Customer <span className="text-red-500">*</span></p>
                        {(prePopulatedCustomer || (preSelectedTransactions && preSelectedTransactions.length > 0)) ? (
                          <div className="space-y-2">
                            <p className="text-lg font-semibold">[{formData.customerAccountNumber}] {formData.customerName}</p>
                            <Badge variant="secondary" className="text-xs">Pre-selected</Badge>
                          </div>
                        ) : (
                          <CustomerSearchCombobox
                            selectedCustomer={selectedCustomer}
                            onSelectCustomer={handleCustomerSelect}
                            placeholder="Select a customer..."
                          />
                        )}
                      </div>

                      <Separator />

                      {/* Contact Name */}
                      <div className="space-y-2">
                        <Label htmlFor="contact" className="text-xs font-medium text-muted-foreground">
                          Contact Name <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={selectedContactId}
                          onValueChange={handleContactChange}
                          disabled={!hasCustomer || apiContacts.length === 0}
                        >
                          <SelectTrigger id="contact">
                            <SelectValue placeholder={getContactPlaceholder(hasCustomer, apiContacts.length)} />
                          </SelectTrigger>
                          <SelectContent>
                            {apiContacts.map((contact) => (
                              <SelectItem key={contact.id} value={contact.id}>
                                {`${contact.firstName} ${contact.surname}`}
                                {contact.isPrimary && " (Primary)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Separator />

                      {/* Email Address (Read-only, derived from contact) */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          Email Address
                        </Label>
                        <Input
                          id="email"
                          value={formData.customerEmail}
                          readOnly
                          disabled
                          className="h-9 bg-muted"
                          placeholder="Select a contact to view email"
                        />
                      </div>

                      {/* Phone Number (Read-only, derived from contact) */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          value={formData.customerPhone}
                          readOnly
                          disabled
                          className="h-9 bg-muted"
                          placeholder="Select a contact to view phone"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Case Management */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Case Management
                    </CardTitle>
                    <CardDescription>Case details and configuration</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-6 md:grid-cols-2">
                      {/* First Column */}
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Outstanding Amount</p>
                          <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                            {formatCurrency(selectedOutstandingTotal, filteredPreSelectedTransactions?.[0]?.currency || selectedCompany?.currency || 'GBP')}
                          </p>
                          <p className="text-xs text-muted-foreground">From selected transactions</p>
                        </div>

                        <Separator />

                        <div className="space-y-2">
                          <Label htmlFor="assignedTo" className="text-xs font-medium text-muted-foreground">
                            Assigned To <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={formData.assignedTo || ""}
                            onValueChange={(value) =>
                              setFormData({ ...formData, assignedTo: value })
                            }
                          >
                            <SelectTrigger id="assignedTo">
                              <SelectValue placeholder="Please select" />
                            </SelectTrigger>
                            <SelectContent>
                              {activeUsers.map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-5 w-5">
                                      <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                      <AvatarFallback className="text-[10px]">
                                        {user.firstName[0]}{user.lastName[0]}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span>{user.firstName} {user.lastName}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <Separator />

                        <div className="space-y-2">
                          <Label htmlFor="status" className="text-xs font-medium text-muted-foreground">
                            Case Status
                          </Label>
                          <Select
                            value={formData.status}
                            onValueChange={(value: 'Active' | 'On Hold' | 'Closed') =>
                              setFormData({ ...formData, status: value })
                            }
                          >
                            <SelectTrigger id="status">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Active">Active</SelectItem>
                              <SelectItem value="Escalated">Escalated</SelectItem>
                              <SelectItem value="On Hold">On Hold</SelectItem>
                              <SelectItem value="Closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="priority" className="text-xs font-medium text-muted-foreground">
                            Priority Level
                          </Label>
                          <Select
                            value={formData.priority}
                            onValueChange={(value: 'High' | 'Medium' | 'Low') =>
                              setFormData({ ...formData, priority: value })
                            }
                          >
                            <SelectTrigger id="priority">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="High">High</SelectItem>
                              <SelectItem value="Medium">Medium</SelectItem>
                              <SelectItem value="Low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Second Column */}
                      <div className="space-y-3">
                        {/* Intentionally left empty to match existing layout */}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Next Action - Call to Action Style */}
              <Card className="border-2 border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Next Scheduled Action
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="nextAction" className="text-xs font-medium text-muted-foreground">
                        Action Type <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.nextAction}
                        onValueChange={(value) => setFormData({ ...formData, nextAction: value })}
                      >
                        <SelectTrigger id="nextAction">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Follow-up call">Follow-up call</SelectItem>
                          <SelectItem value="Send email">Send email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nextActionDate" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Due Date <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="nextActionDate"
                        type="date"
                        value={formData.nextActionDate}
                        onChange={(e) => setFormData({ ...formData, nextActionDate: e.target.value })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    Case Notes
                  </CardTitle>
                  <CardDescription>Add notes about this case</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Add notes about this case..."
                    rows={4}
                    className="resize-none"
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="transactions" className="space-y-4 mt-0">
              <Card className="min-w-0 overflow-hidden">
                <CardHeader>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <ArrowRightLeft className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <CardTitle className="text-lg">Outstanding Transactions</CardTitle>
                      {hasCustomer && (
                        <Badge variant="secondary" className="ml-2">
                          {txPagination.totalItems}
                        </Badge>
                      )}
                    </div>
                    {hasCustomer && (
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="search"
                            placeholder="Quick search..."
                            value={transactionSearch}
                            onChange={(e) => { setTransactionSearch(e.target.value); setTxCurrentPage(1); }}
                            className="pl-8 h-9 w-[200px]"
                          />
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-9 w-9"
                              title="Export to Excel"
                            >
                              <ArrowDownToLine className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleExportCurrentView}>
                              Export Current View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleExportAllColumns}>
                              Export All Columns
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <ColumnConfigDialog
                          columns={caseTxCols}
                          onApply={handleColumnConfigApply}
                          onReset={resetCaseTxPrefs}
                        />
                      </div>
                    )}
                  </div>
                  {!hasCustomer && (
                    <CardDescription>
                      Select a customer first to view transactions
                    </CardDescription>
                  )}
                </CardHeader>
                {hasCustomer && (
                <CardContent className="overflow-x-auto">
                  <TransactionTable
                    transactions={graphqlTransactions}
                    columnVisibility={columnVisibility}
                    orderedColumns={caseTxCols}
                    onViewTransaction={handleViewTransactionDetail}
                    showCustomerColumn={false}
                    displayAmounts={true}
                    enableSelection={true}
                    selectedRows={new Set(Array.from(selectedTransactions).map(x => Number(x)))}
                    onSelectRow={handleToggleTransaction}
                    onSelectAll={(checked) => checked ? handleSelectAllTransactions() : handleDeselectAllTransactions()}
                    currentPage={txCurrentPage}
                    pageSize={txItemsPerPage}
                    totalItems={txPagination.totalItems}
                    onPageChange={setTxCurrentPage}
                    onPageSizeChange={(size) => { setTxItemsPerPage(size); setTxCurrentPage(1); }}
                    onSortChange={onSortChange}
                    renderActions={(transaction) => (
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewTransactionDetail(transaction); }}>
                        View
                      </Button>
                    )}
                  />

                  <Separator className="my-4" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Outstanding:</span>
                    <span className="text-xl font-bold text-red-600 dark:text-red-400">
                      {formatCurrency(selectedOutstandingTotal)}
                    </span>
                  </div>
                </CardContent>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Activity Timeline</CardTitle>
                  <CardDescription>
                    Activity history will appear here after the case is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No activity yet</p>
                    <p className="text-xs mt-1">Activities will be logged once the case is created</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="audit" className="space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>
                    Audit trail will be available after the case is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No audit entries yet</p>
                    <p className="text-xs mt-1">All case modifications will be logged after creation</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </ExportProvider>
      </SheetContent>

      {/* Confirmation Dialog for Unsaved Changes */}
      <AlertDialog open={showCloseConfirmation} onOpenChange={setShowCloseConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving? All your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transaction Detail Sheet */}
      <TransactionDetailSheet
        open={transactionDetailSheetOpen}
        onOpenChange={setTransactionDetailSheetOpen}
        transaction={selectedTransactionDetail}
      />
    </Sheet>
  );
}
